<template>
  <v-container>
    <GoBackAtTop :to="{name: 'Start'}" :label="$t('login.password.back-button')" />
    <h1>{{ $t('login.password.header') }}</h1>
    <p>{{ $t('login.password.clarification') }}</p>
    <p>
      <!-- <p :href="null" target="_blank">{{ $t('login.password.more-info') }}</a> -->
    </p>
    <h4 class="pb-2">{{ $t('login.password.log-in') }}</h4>
    <v-form ref="form">
      <v-form ref="emailForm">
        <v-text-field
          outlined
          :label="$t('login.password.email')"
          v-model="email"
          :rules="[rules.required, rules.email]"
          :disabled="isLoading"
          @input="clearErrorMessages"
        />
      </v-form>
      <div v-if="capsIsOn" class="caps-lock">
        <span>{{ $t('login.password.caps-lock') }}</span>
      </div>
      <v-text-field
        id="password"
        :type="showPassword ? 'text' : 'password'"
        :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
        outlined
        :label="$t('login.password.password')"
        v-model="password"
        :rules="[rules.required]"
        @click:append="showPassword = !showPassword"
        @input="clearErrorMessages"
        :disabled="isLoading"
        :error-messages="error"
        class="mb-0"
      />
      <div class="d-flex justify-end mb-9">
        <v-btn text color="black" @click="showRequestSmsModal" style="height: 24px">{{
          $t('login.password.forgot-password-button')
        }}</v-btn>
      </div>
      <ActionButton @click="login" actionIcon="$sl-login" :isDisabled="isLoading">{{
        $t('login.password.log-in')
      }}</ActionButton>
    </v-form>
    <p class="mt-12">
      <a href="https://www.byggbranschensyrkesnamnd.se/kontakt/kontakta-regionerna/" target="_blank">{{
        $t('login.password.problems')
      }}</a>
    </p>
    <ForgotPasswordModal :show="showForgotPasswordModal" @close="closeRequestSmsModal" />
    <SmsCodeModal
      :show="showSmsCodeModal"
      @close="closeSmsCodeModal"
      :mobilephone="mobilephone"
      :lastSms="lastSmsSentAt"
      @sendSms="updateSmsTimestamp"
    />
    <NewPasswordModal :show="showNewPasswordModal" @close="closeNewPasswordModal" :resetToken="resetToken" />
    <RestoreSuccessModal :show="showRestoreSuccessModal" @close="closeRestoreSuccessModal" />
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import {RulesMixin} from '@/components/mixins'

import ActionButton from '@/components/Common/ActionButton'
import GoBackAtTop from '@/components/Common/GoBackAtTop'
import ForgotPasswordModal from '@/components/Start/RestorePassword/ForgotPasswordModal'
import SmsCodeModal from '@/components/Start/RestorePassword/SmsCodeModal'
import NewPasswordModal from '@/components/Start/RestorePassword/NewPasswordModal'
import RestoreSuccessModal from '@/components/Start/RestorePassword/RestoreSuccessModal'

export default {
  name: 'Login',

  mixins: [RulesMixin],

  components: {
    ActionButton,
    GoBackAtTop,
    ForgotPasswordModal,
    SmsCodeModal,
    NewPasswordModal,
    RestoreSuccessModal
  },

  data: () => ({
    email: '',
    password: '',
    showPassword: false,
    showForgotPasswordModal: false,
    showSmsCodeModal: false,
    showNewPasswordModal: false,
    showRestoreSuccessModal: false,
    mobilephone: '',
    lastSmsSentAt: null,
    isLoading: false,
    error: '',
    resetToken: '',
    capslockListener: null,
    capsIsOn: false
  }),

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isLoggedIn'])
  },

  created() {
    if (this.isLoggedIn) {
      this.$nextTick(() => {
        this.$router.push({
          name: 'Start'
        })
      })
    }
    this.addCapslockListener()
  },
  beforeDestroy() {
    this.removeCapslockListener()
  },
  methods: {
    ...mapActions('currentuser', ['setCurrentUser']),
    showRequestSmsModal() {
      this.showForgotPasswordModal = true
    },
    closeRequestSmsModal(result) {
      this.showForgotPasswordModal = false
      if (!result) {
        return
      }
      this.mobilephone = result.mobilephone
      this.lastSmsSentAt = result.sentTime
      this.showSmsCodeModal = true
    },
    closeSmsCodeModal(result) {
      this.showSmsCodeModal = false
      if (!result) {
        return
      }
      this.resetToken = result.token
      this.showNewPasswordModal = true
    },
    closeNewPasswordModal(result) {
      this.showNewPasswordModal = false
      if (!result) {
        return
      }
      this.showRestoreSuccessModal = true
    },
    closeRestoreSuccessModal() {
      this.$refs.form.reset()
      this.showRestoreSuccessModal = false
    },
    updateSmsTimestamp(timestamp) {
      this.lastSmsSentAt = timestamp
    },
    login() {
      const valid = this.$refs.form.validate()
      if (!valid) return
      this.isLoading = true
      API.passwordLogin({email: this.email, password: this.password})
        .then(({data}) => {
          this.setCurrentUser(data)
          this.$nextTick(() => {
            this.$router.push({
              name: 'Start'
            })
          })
        })
        .catch(({response = {}}) => {
          const {data} = response
          this.error = data || 'Fel vid försök till inloggning.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    clearErrorMessages() {
      this.error = ''
    },
    addCapslockListener() {
      this.$nextTick(() => {
        const password = document.querySelector('#password')
        this.capslockListener = password.addEventListener('keydown', e => {
          if (e.getModifierState('CapsLock')) {
            this.capsIsOn = true
          } else {
            this.capsIsOn = false
          }
        })
      })
    },
    removeCapslockListener() {
      const password = document.querySelector('#password')
      if (password) {
        password.removeEventListener('keydown', this.capslockListener)
      }
    }
  }
}
</script>

<style lang="scss">
.caps-lock {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  span {
    background-color: rgba(0, 0, 0, 0.75);
    color: #fff;
    height: 24px;
    border-radius: 4px;
    padding: 2px 10px;
    position: absolute;
    top: -26px;
  }
}
</style>